import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Inject, Component, ViewEncapsulation, ViewChild, Directive, InjectFlags, Optional, Input, ContentChild, EventEmitter, Output, NgModule } from '@angular/core';
import * as i3 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i2 from '@angular/material/core';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import * as i1 from '@angular/material/datepicker';
import { MAT_DATE_RANGE_SELECTION_STRATEGY, DefaultMatCalendarRangeStrategy, DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { XPO_DATE_ADAPTER_DATE_FORMATS_PROVIDER_FACTORY } from '@xpo-ltl/ngx-ltl-core/utils';
import { Subject, merge } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i2$1 from '@angular/material/form-field';
import { MAT_FORM_FIELD, MatFormFieldControl } from '@angular/material/form-field';
import { takeUntil } from 'rxjs/operators';
import * as i1$1 from '@angular/forms';
import { NgControl, Validators, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import * as i1$2 from '@angular/cdk/overlay';
import { OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

/**
 * Manages calendar header events, dates format and others headers utilities.
 * Allows to communicate each calendar header component with its parent calendar component
 * to keep both calendar header synchronized.
 */
class XpoCalendarHeaderService {
    constructor(intl, dateAdapter, dateFormats) {
        this.intl = intl;
        this.dateAdapter = dateAdapter;
        this.dateFormats = dateFormats;
        /** Subject to notify when to move to the next period */
        this.onNext = new Subject();
        /** Subject to notify when to move to the previous period */
        this.onPrevious = new Subject();
    }
    /**
     * Returns if header previous button should be enabled,
     * based on the calendar min date
     * @param minDate
     * @param activeDate
     */
    previousEnabled(minDate, activeDate) {
        // get the first day of the month
        const firstDate = this.dateAdapter.createDate(this.dateAdapter.getYear(activeDate), this.dateAdapter.getMonth(activeDate), 1);
        // if min date is earlier than the first day of the month we enable the previous button
        return !minDate || this.dateAdapter.compareDate(minDate, firstDate) < 0;
    }
    /**
     * Returns if header next button should be enabled,
     * based on the calendar max date
     * @param maxDate
     * @param activeDate
     */
    nextEnabled(maxDate, activeDate) {
        // get the last day of the month
        const lastDate = this.dateAdapter.createDate(this.dateAdapter.getYear(activeDate), this.dateAdapter.getMonth(activeDate), this.dateAdapter.getNumDaysInMonth(activeDate));
        // if max date is later than the last day of the month we enable the next button
        return !maxDate || this.dateAdapter.compareDate(maxDate, lastDate) > 0;
    }
    /**
     * Returns the aria-label for the previous button
     */
    getPreviousAriaLabel() {
        return this.intl.prevMonthLabel;
    }
    /**
     * Returns the aria-label for the next button
     */
    getNextAriaLabel() {
        return this.intl.nextMonthLabel;
    }
    /**
     * Returns the formatted date to show as the calendar period title
     * @param date
     */
    getPeriodLabel(date) {
        return this.dateAdapter.format(date, this.dateFormats.display.monthYearLabel);
    }
}
XpoCalendarHeaderService.ɵfac = function XpoCalendarHeaderService_Factory(t) { return new (t || XpoCalendarHeaderService)(i0.ɵɵinject(i1.MatDatepickerIntl), i0.ɵɵinject(i2.DateAdapter), i0.ɵɵinject(MAT_DATE_FORMATS)); };
XpoCalendarHeaderService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: XpoCalendarHeaderService, factory: XpoCalendarHeaderService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoCalendarHeaderService, [{
        type: Injectable
    }], function () { return [{ type: i1.MatDatepickerIntl }, { type: i2.DateAdapter }, { type: undefined, decorators: [{
                type: Inject,
                args: [MAT_DATE_FORMATS]
            }] }]; }, null); })();

/**
 * Header component to be used as custom header inside each mat-calendar
 */
class XpoDateRangeCalendarHeader {
    constructor(headerService, calendar, changeDetectorRef) {
        this.headerService = headerService;
        this.calendar = calendar;
        this.changeDetectorRef = changeDetectorRef;
        this.calendar.stateChanges.subscribe(() => this.changeDetectorRef.detectChanges());
    }
    /**
     * Returns the text to be shown as the period title
     */
    get periodLabel() {
        return this.headerService.getPeriodLabel(this.calendar.activeDate).toLocaleUpperCase();
    }
    /**
     * Returns the aria-label for the previous button
     */
    get prevButtonAriaLabel() {
        return this.headerService.getPreviousAriaLabel();
    }
    /**
     * Returns the aria-label for the next button
     */
    get nextButtonAriaLabel() {
        return this.headerService.getNextAriaLabel();
    }
    /**
     * Returns if the previous button should be enabled
     */
    previousEnabled() {
        return this.headerService.previousEnabled(this.calendar.minDate, this.calendar.activeDate);
    }
    /**
     * Returns if the next button should be enabled
     */
    nextEnabled() {
        return this.headerService.nextEnabled(this.calendar.maxDate, this.calendar.activeDate);
    }
    /**
     * Handles the previous button click
     */
    onPreviousClicked() {
        // notify to show the previous period
        this.headerService.onPrevious.next();
    }
    /**
     * Handles the next button click
     */
    onNextClicked() {
        // notify to show the next period
        this.headerService.onNext.next();
    }
}
XpoDateRangeCalendarHeader.ɵfac = function XpoDateRangeCalendarHeader_Factory(t) { return new (t || XpoDateRangeCalendarHeader)(i0.ɵɵdirectiveInject(XpoCalendarHeaderService), i0.ɵɵdirectiveInject(i1.MatCalendar), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
XpoDateRangeCalendarHeader.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: XpoDateRangeCalendarHeader, selectors: [["xpo-date-range-calendar-header"]], hostAttrs: [1, "xpo-DateRangeCalendarHeader"], exportAs: ["xpoDateRangeCalendarHeader"], decls: 5, vars: 5, consts: [[1, "xpo-DateRangeCalendarHeader-controls"], ["mat-icon-button", "", 1, "xpo-DateRangeCalendarHeader-prev", 3, "disabled", "click"], [1, "xpo-DateRangeCalendarHeader-period"], ["mat-icon-button", "", 1, "xpo-DateRangeCalendarHeader-next", 3, "disabled", "click"]], template: function XpoDateRangeCalendarHeader_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "button", 1);
        i0.ɵɵlistener("click", function XpoDateRangeCalendarHeader_Template_button_click_1_listener() { return ctx.onPreviousClicked(); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵtext(3);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "button", 3);
        i0.ɵɵlistener("click", function XpoDateRangeCalendarHeader_Template_button_click_4_listener() { return ctx.onNextClicked(); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("disabled", !ctx.previousEnabled());
        i0.ɵɵattribute("aria-label", ctx.prevButtonAriaLabel);
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(ctx.periodLabel);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("disabled", !ctx.nextEnabled());
        i0.ɵɵattribute("aria-label", ctx.nextButtonAriaLabel);
    } }, directives: [i3.MatButton], styles: [".flex--stretch[_ngcontent-%COMP%]{flex:1}.xpo-DateRangeCalendarHeader-controls[_ngcontent-%COMP%]{align-items:center;display:flex;flex-direction:row;height:30px;margin-top:8px}.xpo-DateRangeCalendarHeader-period[_ngcontent-%COMP%]{color:#000;flex-grow:1;font-weight:500;text-align:center}.xpo-DateRangeCalendarHeader-prev[_ngcontent-%COMP%], .xpo-DateRangeCalendarHeader-next[_ngcontent-%COMP%]{height:16px;width:16px}.xpo-DateRangeCalendarHeader-prev[_ngcontent-%COMP%]:after, .xpo-DateRangeCalendarHeader-next[_ngcontent-%COMP%]:after{border-top:3px solid #000000;bottom:0;content:\"\";left:0;position:absolute;right:0;top:0}.xpo-DateRangeCalendarHeader-prev.mat-button-disabled[_ngcontent-%COMP%]:after, .xpo-DateRangeCalendarHeader-next.mat-button-disabled[_ngcontent-%COMP%]:after{border-top-color:#bdbdbd}.xpo-DateRangeCalendarHeader-prev[_ngcontent-%COMP%]:after{border-left:3px solid #000000;transform:scale(.5305) translate(3px) rotate(-45deg)}.xpo-DateRangeCalendarHeader-prev.mat-button-disabled[_ngcontent-%COMP%]:after{border-left-color:#bdbdbd}.xpo-DateRangeCalendarHeader-next[_ngcontent-%COMP%]:after{border-right:3px solid #000000;transform:scale(.5305) translate(-3px) rotate(45deg)}.xpo-DateRangeCalendarHeader-next.mat-button-disabled[_ngcontent-%COMP%]:after{border-right-color:#bdbdbd}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangeCalendarHeader, [{
        type: Component,
        args: [{
                selector: 'xpo-date-range-calendar-header',
                exportAs: 'xpoDateRangeCalendarHeader',
                styleUrls: ['calendar-header.component.scss'],
                templateUrl: 'calendar-header.component.html',
                host: {
                    class: 'xpo-DateRangeCalendarHeader',
                },
            }]
    }], function () { return [{ type: XpoCalendarHeaderService }, { type: i1.MatCalendar }, { type: i0.ChangeDetectorRef }]; }, null); })();

const _c0$1 = ["start"];
const _c1$1 = ["end"];
/**
 * Two calendars component that allows to select a date range
 */
class XpoDateRangeCalendar {
    constructor(headerService, dateAdapter, changeDetector) {
        this.headerService = headerService;
        this.dateAdapter = dateAdapter;
        this.changeDetector = changeDetector;
        // listen to period changes
        this.headerService.onNext.subscribe(() => this.setNextPeriod());
        this.headerService.onPrevious.subscribe(() => this.setPreviousPeriod());
    }
    ngAfterViewInit() {
        // set the period to show for both calendars
        // taking into account if there is a date range already selected
        this.showCurrentPeriod();
        // set focus on start calendar
        this.startCalendar.focusActiveCell();
        // need to handle preview changes for both start and end calendar
        this.handlePreviewChange();
    }
    /**
     * Returns the ComponentType for the calendar header
     */
    getHeaderComponentType() {
        // the custom component header type
        return XpoDateRangeCalendarHeader;
    }
    /**
     * Handles when selection changes inside the calendar
     * @param event
     */
    handleUserSelection(event) {
        // updates the current selection with the new date
        this.selectionModel.changeSelection(event.value);
        // if the selection range is completed
        if (this.selectionModel.isComplete()) {
            // close the date range picker
            this.dateRangePicker.close();
        }
    }
    /**
     * Returns the current selected range
     */
    getSelected() {
        return this.selectionModel.selection;
    }
    /**
     * Sets the reference to the date range picker that contains the calendar and its selection model
     * @param dateRangePicker
     */
    registerDateRangePicker(dateRangePicker) {
        this.dateRangePicker = dateRangePicker;
        // keep the selection model
        this.selectionModel = dateRangePicker.selectionModel;
        // set the min and max dates for the start calendar first
        this.startCalendarMin = this.dateRangePicker.getMinDate();
        this.startCalendarMax = this.dateRangePicker.getMaxDate();
    }
    /**
     * Initialize the period to be shown in both start and end calendars
     * If there is a selected range we'll see the first calendar based on the start date
     */
    showCurrentPeriod() {
        if (this.selectionModel.selection.start) {
            // set the active date for the start calendar
            // if not it keeps being the current date (as default)
            this.startCalendar.activeDate = this.selectionModel.selection.start;
        }
        // move end calendar one month after the start calendar
        this.endCalendar.activeDate = this.getNextMonth(this.startCalendar.activeDate);
        // to avoid mat-calendar clamp the active date between the min and max
        // we set them after moving the end calendar
        // dates are the same for both calendars
        this.endCalendarMin = this.startCalendarMin;
        this.endCalendarMax = this.startCalendarMax;
        // force to detect changes inside the mat-calendar
        this.changeDetector.detectChanges();
    }
    /**
     * Sets the period to be shown when user clicks on next
     */
    setNextPeriod() {
        // move the start calendar
        this.startCalendar.activeDate = this.getNextMonth(this.startCalendar.activeDate);
        // move the end calendar
        this.endCalendar.activeDate = this.getNextMonth(this.endCalendar.activeDate);
    }
    /**
     * Sets the period to be shown when user clicks on previous
     */
    setPreviousPeriod() {
        // move the start calendar
        this.startCalendar.activeDate = this.getPreviousMonth(this.startCalendar.activeDate);
        // move the end calendar
        this.endCalendar.activeDate = this.getPreviousMonth(this.endCalendar.activeDate);
    }
    /**
     * Handle when the selected range preview changes inside the calendar
     */
    handlePreviewChange() {
        // if preview change is made in the end calendar,
        // need to reflect this in the start calendar too
        this.endCalendar.monthView._matCalendarBody.previewChange.subscribe((event) => {
            this.startCalendar.monthView._previewChanged(event);
        });
    }
    /**
     * Based on the given date, returns the next month
     * @param date
     */
    getNextMonth(date) {
        // to the given date, add one month
        return this.dateAdapter.addCalendarMonths(date, 1);
    }
    /**
     * Based on the given date, returns the previous month
     * @param date
     */
    getPreviousMonth(date) {
        // to the given date, subtract one month
        return this.dateAdapter.addCalendarMonths(date, -1);
    }
}
XpoDateRangeCalendar.ɵfac = function XpoDateRangeCalendar_Factory(t) { return new (t || XpoDateRangeCalendar)(i0.ɵɵdirectiveInject(XpoCalendarHeaderService), i0.ɵɵdirectiveInject(i2.DateAdapter), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
XpoDateRangeCalendar.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: XpoDateRangeCalendar, selectors: [["xpo-date-range-calendar"]], viewQuery: function XpoDateRangeCalendar_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0$1, 5);
        i0.ɵɵviewQuery(_c1$1, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.startCalendar = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.endCalendar = _t.first);
    } }, hostAttrs: [1, "xpo-DateRangePickerCalendar"], exportAs: ["xpoDateRangeCalendar"], features: [i0.ɵɵProvidersFeature([
            XpoCalendarHeaderService,
            { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy },
        ])], decls: 5, vars: 8, consts: [[1, "xpo-DateRangePickerCalendar-wrapper"], ["id", "startRangeCalendar", 3, "minDate", "maxDate", "headerComponent", "selected", "_userSelection"], ["start", ""], ["id", "endRangeCalendar", 3, "minDate", "maxDate", "headerComponent", "selected", "_userSelection"], ["end", ""]], template: function XpoDateRangeCalendar_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "mat-calendar", 1, 2);
        i0.ɵɵlistener("_userSelection", function XpoDateRangeCalendar_Template_mat_calendar__userSelection_1_listener($event) { return ctx.handleUserSelection($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "mat-calendar", 3, 4);
        i0.ɵɵlistener("_userSelection", function XpoDateRangeCalendar_Template_mat_calendar__userSelection_3_listener($event) { return ctx.handleUserSelection($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("minDate", ctx.startCalendarMin)("maxDate", ctx.startCalendarMax)("headerComponent", ctx.getHeaderComponentType())("selected", ctx.getSelected());
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("minDate", ctx.endCalendarMin)("maxDate", ctx.endCalendarMax)("headerComponent", ctx.getHeaderComponentType())("selected", ctx.getSelected());
    } }, directives: [i1.MatCalendar], styles: [".flex--stretch{flex:1}.xpo-DateRangePickerCalendar{background-color:#fff;border:1px solid #000000}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper{display:inline-flex;flex-direction:row}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar{height:293px;width:260px}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar:first-of-type .mat-calendar-content{padding-right:4px}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar:first-of-type .xpo-DateRangeCalendarHeader-next{display:none}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar:first-of-type .xpo-DateRangeCalendarHeader-controls{padding-left:8px;padding-right:4px}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar:last-of-type{border-left:2px solid #d8d8d8}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar:last-of-type .mat-calendar-content{padding-left:4px}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar:last-of-type .xpo-DateRangeCalendarHeader-prev{display:none}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar:last-of-type .xpo-DateRangeCalendarHeader-controls{padding-left:4px;padding-right:8px}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar-content{padding-top:8px}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-table-header{color:#000}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-table-header tr th{font-size:1rem;font-weight:500;padding-bottom:4px}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-table-header tr:last-of-type{display:none}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body{color:#000;font-size:1rem}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-label{padding:0!important;visibility:hidden}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-cell-content{background-color:#fafafa}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-cell-content:hover{border-color:#bdbdbd}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-today:not(.mat-calendar-body-selected){background-color:#fff;border-color:#333;color:#333}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-today:not(.mat-calendar-body-selected):hover{border-color:#bdbdbd}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-selected{background-color:#1976d2;color:#fff;font-weight:500}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-today.mat-calendar-body-selected{box-shadow:none}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-in-range:before,.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-in-range .mat-calendar-body-cell-content:not(.mat-calendar-body-selected){background-color:#e4effa}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-in-range:first-of-type:not(.mat-calendar-body-range-start):before,.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-in-range:first-of-type:not(.mat-calendar-body-range-start):after,.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-label+td.mat-calendar-body-in-range:not(.mat-calendar-body-range-start):before,.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-label+td.mat-calendar-body-in-range:not(.mat-calendar-body-range-start):after{border-bottom-left-radius:999px;border-top-left-radius:999px;left:5%}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body .mat-calendar-body-range-start:before{width:100%}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-in-range:last-of-type:not(.mat-calendar-body-range-end):before,.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-in-range:last-of-type:not(.mat-calendar-body-range-end):after{border-bottom-right-radius:999px;border-top-right-radius:999px;width:95%}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body-in-preview .mat-calendar-body-cell-preview{border-bottom:1px dashed #bdbdbd;border-top:1px dashed #bdbdbd;z-index:999}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body .mat-calendar-body-preview-start .mat-calendar-body-cell-preview{border-left:1px dashed #bdbdbd}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body .mat-calendar-body-preview-end .mat-calendar-body-cell-preview{border-right:1px dashed #bdbdbd}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-in-preview:first-of-type:not(.mat-calendar-body-preview-start) .mat-calendar-body-cell-preview,.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-label+td.mat-calendar-body-in-preview:not(.mat-calendar-body-preview-start) .mat-calendar-body-cell-preview{border-bottom-left-radius:999px;border-left:1px dashed #bdbdbd;border-top-left-radius:999px;left:5%}.xpo-DateRangePickerCalendar .xpo-DateRangePickerCalendar-wrapper .mat-calendar .mat-calendar-content mat-month-view .mat-calendar-body tr td.mat-calendar-body-in-preview:last-of-type:not(.mat-calendar-body-preview-end) .mat-calendar-body-cell-preview{border-bottom-right-radius:999px;border-right:1px dashed #bdbdbd;border-top-right-radius:999px;width:95%}\n"], encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangeCalendar, [{
        type: Component,
        args: [{
                selector: 'xpo-date-range-calendar',
                exportAs: 'xpoDateRangeCalendar',
                templateUrl: 'date-range-calendar.component.html',
                styleUrls: ['date-range-calendar.component.scss'],
                host: {
                    class: 'xpo-DateRangePickerCalendar',
                },
                // Needed to override mat-calendar styles
                encapsulation: ViewEncapsulation.None,
                providers: [
                    XpoCalendarHeaderService,
                    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy },
                ],
            }]
    }], function () { return [{ type: XpoCalendarHeaderService }, { type: i2.DateAdapter }, { type: i0.ChangeDetectorRef }]; }, { startCalendar: [{
            type: ViewChild,
            args: ['start']
        }], endCalendar: [{
            type: ViewChild,
            args: ['end']
        }] }); })();

/**
 * Validates if the selected range is valid,
 * i.e. start date should be earlier than the end date
 * */
function xpoDateRangeInvalidValidator(selectionModel, dateAdapter) {
    return () => {
        // if selection model is not valid, we can't do validation
        if (!selectionModel) {
            return null;
        }
        // define the error
        const err = { xpoDateRangeInvalid: true };
        // keep start and end values from selection
        const { start, end } = selectionModel.selection;
        // evaluates if start date is later than end date
        return start && end && dateAdapter.compareDate(start, end) > 0 ? err : null;
    };
}

/**
 * Validates if the date value is later than the max date
 * */
function xpoDateRangeMaxDateValidator(date, maxDate, dateAdapter) {
    return () => 
    // evaluates if the control value is later than the max date
    date && maxDate && dateAdapter.compareDate(date, maxDate) > 0 ?
        { xpoDateRangeMaxDateError: true } : null;
}

/**
 * Validates if the date value is early than the min date
 * */
function xpoDateRangeMinDateValidator(date, minDate, dateAdapter) {
    return () => 
    // evaluates if the control value is early than the min value
    date && minDate && dateAdapter.compareDate(date, minDate) < 0 ?
        { xpoDateRangeMinDateError: true } : null;
}

/**
 * Validates if there was some error parsing dates typed by the user or assigned by value setter
 * */
function xpoDateRangeParseValidator(hasParseError) {
    return () => 
    // evaluates if there was some error
    hasParseError ? { xpoDateRangeParseError: true } : null;
}

/**
 * Validates if the selection range is uncomplete,
 * i.e. one of the date has value, and the other one hasn't
 * */
function xpoDateRangeUncompletedValidator(selectionModel) {
    return () => {
        // if selection model is not valid, we can't do validation
        if (!selectionModel) {
            return null;
        }
        // define the error
        const err = { xpoDateRangeUncompleted: true };
        // keep start and end values from selection
        const { start, end } = selectionModel.selection;
        // evaluates if one of the date range is null and the other is not
        return (start && !end) || (end && !start) ? err : null;
    };
}

/**
 * Directive that allows supress the default placeholder for inputs
 */
class XpoDateRangeInputSupressPlaceholder {
}
XpoDateRangeInputSupressPlaceholder.ɵfac = function XpoDateRangeInputSupressPlaceholder_Factory(t) { return new (t || XpoDateRangeInputSupressPlaceholder)(); };
XpoDateRangeInputSupressPlaceholder.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoDateRangeInputSupressPlaceholder, selectors: [["input", "xpoDateRangeStartInput", "", "xpoDateInputSuprressPlaceholder", ""], ["input", "xpoDateRangeEndInput", "", "xpoDateInputSuprressPlaceholder", ""]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangeInputSupressPlaceholder, [{
        type: Directive,
        args: [{
                selector: 'input[xpoDateRangeStartInput][xpoDateInputSuprressPlaceholder],input[xpoDateRangeEndInput][xpoDateInputSuprressPlaceholder]',
            }]
    }], null, null); })();

/**
 * Base class for each child input control in the date range input form field
 */
class XpoDateRangeBaseInput {
    constructor(elementRef, parentForm, parentFormGroup, dateAdapter, dateFormats, injector, errorStateMatcher, suppressPlaceholder) {
        this.elementRef = elementRef;
        this.parentForm = parentForm;
        this.parentFormGroup = parentFormGroup;
        this.dateAdapter = dateAdapter;
        this.dateFormats = dateFormats;
        this.injector = injector;
        this.errorStateMatcher = errorStateMatcher;
        this.suppressPlaceholder = suppressPlaceholder;
        this.XPO_DATE_RANGE_PICKER_INPUT_PLACEHOLDER = 'MM/DD/YYYY';
        /**
         * Whether the input has focus
         */
        this.focused = false;
        /** Whether the input is in an error state. */
        this.errorState = false;
        /** Whether the last value set on the input is a valid date. */
        this.hasParseError = false;
        /**
         * Callback function that is called by the forms API on initialization to update the form model when opening calendar.
         * Used as part of the implementation of registerOnTouched() for ControlValueAccessor.
         */
        this.onTouched = () => { };
        /** Callback function that is called when the control's value changes in the UI
         * Used as part of the implementation of registerOnChange() for ControlValueAccessor.
         */
        this.cvaOnChange = () => { };
        /**
         * Callback function to call when the validator inputs change
         * Used as part of the implementation of registerOnValidatorChange() for Validator.
         * */
        this.validatorOnChange = () => { };
        /** Emits when the internal state has changed */
        this.stateChanges = new Subject();
    }
    /**
     * The value of the input.
     */
    get value() {
        // check in case the model is not registered, return the pending value if exists or null
        return this.selectionModel ? this.getValueFromRange(this.selectionModel.selection) : this.pendingValue;
    }
    set value(value) {
        // deserialize the value to a valid date object
        let date = this.dateAdapter.deserialize(value);
        // check if the date is valid for validation purposes
        this.hasParseError = !this.isValidDate(date);
        // check if the date is a valid, if not return null
        date = this.dateAdapter.getValidDateOrNull(date);
        // change de current selection in the model
        this.changeSelection(date);
        // update the element value with the formatted date
        this.setInputFormattedValue(date);
    }
    /**
     * Whether the control is required.
     */
    get required() {
        return this.elementRef.nativeElement.required;
    }
    ngOnInit() {
        // (from material): We need the date input to provide itself as a `ControlValueAccessor` and a `Validator`, while
        // injecting its `NgControl` so that the error state is handled correctly. This introduces a
        // circular dependency, because both `ControlValueAccessor` and `Validator` depend on the input
        // itself. Usually we can work around it for the CVA, but there's no API to do it for the
        // validator. We work around it here by injecting the `NgControl` in `ngOnInit`, after
        // everything has been resolved.
        // eslint-disable-next-line no-bitwise
        const ngControl = this.injector.get(NgControl, null, InjectFlags.Self | InjectFlags.Optional);
        if (ngControl) {
            this.ngControl = ngControl;
        }
        // set the default placeholder for the input
        this.setDefaultPlaceholder();
    }
    ngDoCheck() {
        if (this.ngControl) {
            // (from material): We need to re-evaluate this on every change detection cycle, because there are some
            // error triggers that we can't subscribe to (e.g. parent form submissions). This means
            // that whatever logic is in here has to be super lean or we risk destroying the performance.
            this.updateErrorState();
        }
    }
    ngOnDestroy() {
        this.stateChanges.complete();
    }
    /**
     * Sets a new value to the input
     * Implemented as part of ControlValueAccessor
     * @param value
     */
    writeValue(value) {
        // assign the given value to the selection model using value setter
        this.value = value;
    }
    /**
     * Registers a callback function that is called when the control's value changes in the UI
     * Implemented as part of ControlValueAccessor.
     * @param fn
     */
    registerOnChange(fn) {
        this.cvaOnChange = fn;
    }
    /**
     * Registers a callback function that is called by the forms API
     * on initialization to update the form model when the input changes (generally on blur)
     * Implemented as part of ControlValueAccessor.
     * @param fn
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /**
     * Function that is called by the forms API
     * when the control status changes to or from 'DISABLED'.
     * Depending on the status, it enables or disables the appropriate DOM element.
     * Implemented as part of ControlValueAccessor.
     * @param _isDisabled
     */
    setDisabledState(_isDisabled) {
        // this.disabled = _isDisabled;
    }
    /**
     * Performs synchronous validation against the provided control.
     * Implemented as part of Validator
     * @param control
     */
    validate(control) {
        // compose all validators with the updated values they need
        const validator = Validators.compose(this.getValidators());
        return validator(control);
    }
    /**
     * Registers a callback function to call when the validator inputs change.
     * Implemented as part of Validator
     * @param fn
     */
    registerOnValidatorChange(fn) {
        this.validatorOnChange = fn;
    }
    /**
     * Focuses the input.
     **/
    focus() {
        this.elementRef.nativeElement.focus();
    }
    /**
     * Gets whether the input is empty.
     **/
    isEmpty() {
        return this.elementRef.nativeElement.value.length === 0;
    }
    /**
     * Gets the placeholder of the input.
     **/
    getPlaceholder() {
        return this.elementRef.nativeElement.placeholder;
    }
    /**
     * Associate the date picker selection model to the input control
     *
     * @param selectionModel
     */
    registerSelectionModel(selectionModel) {
        // keep the selection model instance
        this.selectionModel = selectionModel;
        // check if there is a pending value assigned before the model is registered
        if (this.pendingValue) {
            this.changeSelection(this.pendingValue);
            this.pendingValue = null;
        }
        // handle selection changes
        this.selectionModel.selectionChanged.subscribe((event) => {
            // check if the change is made outside input controls
            if (event.source !== 'input') {
                this.handleSelectionChanged(event.value);
            }
            else {
                // Whenever the value changes inside one input we need to revalidate, because
                // the validation state of each of the inputs depends on the other one.
                this.validatorOnChange();
            }
        });
    }
    /**
     * Based on the date formats config, set the default placeholder
     */
    setDefaultPlaceholder() {
        // if there is not a placeholder already defined
        if (this.getPlaceholder().length === 0 && !this.suppressPlaceholder) {
            this.elementRef.nativeElement.placeholder = this.XPO_DATE_RANGE_PICKER_INPUT_PLACEHOLDER;
        }
    }
    /**
     * Handles the input event inside the control
     * @param value
     */
    onInput(value) {
        // parse the given value into date instance
        let date = this.dateAdapter.parse(value, this.dateFormats.parse.dateInput);
        // check if the date is valid for validation purposes
        this.hasParseError = !this.isValidDate(date);
        // check if parsed value is valid date or assign null
        date = this.dateAdapter.getValidDateOrNull(date);
        // change the current selection
        this.changeSelection(date);
    }
    /**
     * Handles blur events on the input.
     * */
    onBlur() {
        // Reformat the input only if it has a valid value.
        if (this.value) {
            this.setInputFormattedValue(this.value);
        }
        // change the focused state
        this.focused = false;
    }
    /**
     * Handles focus event on the input
     */
    onFocus() {
        this.focused = true;
    }
    /**
     * Handles selection changes from outside the input control
     */
    handleSelectionChanged(range) {
        // the change comes from the calendar, where the selected dates are valid
        // so reset the parse error flag
        this.hasParseError = false;
        // get the corresponding value for each input
        const date = this.getValueFromRange(range);
        // update the input control value
        this.setInputFormattedValue(date);
        // call the ControlValueAccessor onChange callback function
        // to allow the forms API to update itself
        this.cvaOnChange(date);
    }
    /**
     * Based on the given date, updates the input control value
     * @param date
     */
    setInputFormattedValue(date) {
        this.elementRef.nativeElement.value = date ? this.dateAdapter.format(date, this.dateFormats.display.dateInput) : '';
    }
    /**
     * Sets the given date to the current selection range
     * @param date
     */
    changeSelection(date) {
        // check if the selection model is already registered
        // We may get some incoming values before the model was
        // assigned. Save the value so that we can assign it later.
        if (!this.selectionModel) {
            this.pendingValue = date;
            return;
        }
        // create a new range based on the given date
        const range = this.getNewSelection(date);
        // update the selection inside the model
        this.selectionModel.updateSelection(range, 'input');
        // call the ControlValueAccessor onChange function
        // to allow the forms API to update itself
        this.cvaOnChange(date);
    }
    /**
     * Gets internal validator functions for date range inputs
     */
    getValidators() {
        return [
            // input date is valid
            xpoDateRangeParseValidator(this.hasParseError),
            // input date is early than min date
            xpoDateRangeMinDateValidator(this.value, this.minDate, this.dateAdapter),
            // input date is later than max date
            xpoDateRangeMaxDateValidator(this.value, this.maxDate, this.dateAdapter),
            // date range has just one null date
            xpoDateRangeUncompletedValidator(this.selectionModel),
            // start date earlier than end date
            xpoDateRangeInvalidValidator(this.selectionModel, this.dateAdapter),
        ];
    }
    /**
     * Whether a date is considered valid.
     * Used to check after parse or deserialize a value
     */
    isValidDate(date) {
        return !date || this.dateAdapter.isValid(date);
    }
    /**
     * Sets the error state value for the input
     */
    updateErrorState() {
        // if the input has a parent form
        const form = this.parentFormGroup || this.parentForm;
        // if the input acts as a ControlValueAccessor
        const control = this.ngControl ? this.ngControl.control : null;
        // check the error state
        this.errorState = this.errorStateMatcher.isErrorState(control, form);
        this.stateChanges.next();
    }
    /**
     * Returns the max length for the input control
     * based on the date format in the global date formats configuration object
     */
    getMaxLength() {
        return this.XPO_DATE_RANGE_PICKER_INPUT_PLACEHOLDER.length;
    }
}
XpoDateRangeBaseInput.ɵfac = function XpoDateRangeBaseInput_Factory(t) { return new (t || XpoDateRangeBaseInput)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.NgForm, 8), i0.ɵɵdirectiveInject(i1$1.FormGroupDirective, 8), i0.ɵɵdirectiveInject(i2.DateAdapter), i0.ɵɵdirectiveInject(MAT_DATE_FORMATS), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i2.ErrorStateMatcher), i0.ɵɵdirectiveInject(XpoDateRangeInputSupressPlaceholder, 8)); };
XpoDateRangeBaseInput.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoDateRangeBaseInput, inputs: { value: "value" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangeBaseInput, [{
        type: Directive
    }], function () { return [{ type: i0.ElementRef }, { type: i1$1.NgForm, decorators: [{
                type: Optional
            }] }, { type: i1$1.FormGroupDirective, decorators: [{
                type: Optional
            }] }, { type: i2.DateAdapter }, { type: undefined, decorators: [{
                type: Inject,
                args: [MAT_DATE_FORMATS]
            }] }, { type: i0.Injector }, { type: i2.ErrorStateMatcher }, { type: XpoDateRangeInputSupressPlaceholder, decorators: [{
                type: Optional
            }] }]; }, { value: [{
            type: Input
        }] }); })();

/**
 * The end date input control
 */
class XpoDateRangeEndInput extends XpoDateRangeBaseInput {
    constructor(elementRef, parentForm, parentFormGroup, dateAdapter, dateFormats, injector, errorStateMatcher, suppressPlaceholder) {
        // (from material): this constructor shouldn't be necessary, but ViewEngine doesn't seem to
        // handle DI correctly when it is inherited from `XpoDateRangeBaseInput`. We can drop this
        // constructor once ViewEngine is removed.
        super(elementRef, parentForm, parentFormGroup, dateAdapter, dateFormats, injector, errorStateMatcher, suppressPlaceholder);
        this.elementRef = elementRef;
        this.parentForm = parentForm;
        this.parentFormGroup = parentFormGroup;
        this.dateAdapter = dateAdapter;
        this.dateFormats = dateFormats;
        this.injector = injector;
        this.errorStateMatcher = errorStateMatcher;
        this.suppressPlaceholder = suppressPlaceholder;
    }
    /**
     * Returns the corresponding value: the end selected date
     * @param range
     */
    getValueFromRange(range) {
        return range.end;
    }
    /**
     * Returns a new range selection based on the given date as the end date of the range
     * @param date
     */
    getNewSelection(date) {
        // create a new range based on the given date
        return new DateRange(this.selectionModel.selection.start, date);
    }
}
XpoDateRangeEndInput.ɵfac = function XpoDateRangeEndInput_Factory(t) { return new (t || XpoDateRangeEndInput)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.NgForm, 8), i0.ɵɵdirectiveInject(i1$1.FormGroupDirective, 8), i0.ɵɵdirectiveInject(i2.DateAdapter), i0.ɵɵdirectiveInject(MAT_DATE_FORMATS), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i2.ErrorStateMatcher), i0.ɵɵdirectiveInject(XpoDateRangeInputSupressPlaceholder, 8)); };
XpoDateRangeEndInput.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoDateRangeEndInput, selectors: [["input", "xpoDateRangeEndInput", ""]], hostAttrs: ["type", "text", "autocomplete", "off", 1, "xpo-DateRangeInput-inner"], hostVars: 1, hostBindings: function XpoDateRangeEndInput_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("input", function XpoDateRangeEndInput_input_HostBindingHandler($event) { return ctx.onInput($event.target.value); })("focus", function XpoDateRangeEndInput_focus_HostBindingHandler() { return ctx.onFocus(); })("blur", function XpoDateRangeEndInput_blur_HostBindingHandler() { return ctx.onBlur(); });
    } if (rf & 2) {
        i0.ɵɵattribute("maxlength", ctx.getMaxLength());
    } }, features: [i0.ɵɵProvidersFeature([
            { provide: NG_VALUE_ACCESSOR, useExisting: XpoDateRangeEndInput, multi: true },
            { provide: NG_VALIDATORS, useExisting: XpoDateRangeEndInput, multi: true },
        ]), i0.ɵɵInheritDefinitionFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangeEndInput, [{
        type: Directive,
        args: [{
                selector: 'input[xpoDateRangeEndInput]',
                host: {
                    class: 'xpo-DateRangeInput-inner',
                    type: 'text',
                    autocomplete: 'off',
                    '[attr.maxlength]': 'getMaxLength()',
                    '(input)': 'onInput($event.target.value)',
                    '(focus)': 'onFocus()',
                    '(blur)': 'onBlur()',
                },
                providers: [
                    { provide: NG_VALUE_ACCESSOR, useExisting: XpoDateRangeEndInput, multi: true },
                    { provide: NG_VALIDATORS, useExisting: XpoDateRangeEndInput, multi: true },
                ],
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i1$1.NgForm, decorators: [{
                type: Optional
            }] }, { type: i1$1.FormGroupDirective, decorators: [{
                type: Optional
            }] }, { type: i2.DateAdapter }, { type: undefined, decorators: [{
                type: Inject,
                args: [MAT_DATE_FORMATS]
            }] }, { type: i0.Injector }, { type: i2.ErrorStateMatcher }, { type: XpoDateRangeInputSupressPlaceholder, decorators: [{
                type: Optional
            }] }]; }, null); })();

/**
 * The start date input control
 */
class XpoDateRangeStartInput extends XpoDateRangeBaseInput {
    constructor(elementRef, parentForm, parentFormGroup, dateAdapter, dateFormats, injector, errorStateMatcher, suppressPlaceholder) {
        // (from material): this constructor shouldn't be necessary, but ViewEngine doesn't seem to
        // handle DI correctly when it is inherited from `XpoDateRangeBaseInput`. We can drop this
        // constructor once ViewEngine is removed.
        super(elementRef, parentForm, parentFormGroup, dateAdapter, dateFormats, injector, errorStateMatcher, suppressPlaceholder);
        this.elementRef = elementRef;
        this.parentForm = parentForm;
        this.parentFormGroup = parentFormGroup;
        this.dateAdapter = dateAdapter;
        this.dateFormats = dateFormats;
        this.injector = injector;
        this.errorStateMatcher = errorStateMatcher;
        this.suppressPlaceholder = suppressPlaceholder;
    }
    /**
     * Gets the value that should be used when mirroring the input's size.
     * */
    getMirrorValue() {
        const element = this.elementRef.nativeElement;
        const value = element.value;
        // if the input has not a placeholder defined, use the value defined for dates format
        // just to keep a min width for the input when no placeholder o values are defined
        const placeholder = element.placeholder.length > 0 ? element.placeholder : this.XPO_DATE_RANGE_PICKER_INPUT_PLACEHOLDER;
        // define the input width based on the typed value or on its placeholder
        return value.length > 0 ? value : placeholder;
    }
    /**
     * Returns the corresponding value: the start selected date
     * @param range
     */
    getValueFromRange(range) {
        return range.start;
    }
    /**
     * Returns a new range selection based on the given date as the start date of the range
     * @param date
     */
    getNewSelection(date) {
        // create a new range based on the given date
        return new DateRange(date, this.selectionModel.selection.end);
    }
}
XpoDateRangeStartInput.ɵfac = function XpoDateRangeStartInput_Factory(t) { return new (t || XpoDateRangeStartInput)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.NgForm, 8), i0.ɵɵdirectiveInject(i1$1.FormGroupDirective, 8), i0.ɵɵdirectiveInject(i2.DateAdapter), i0.ɵɵdirectiveInject(MAT_DATE_FORMATS), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i2.ErrorStateMatcher), i0.ɵɵdirectiveInject(XpoDateRangeInputSupressPlaceholder, 8)); };
XpoDateRangeStartInput.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoDateRangeStartInput, selectors: [["input", "xpoDateRangeStartInput", ""]], hostAttrs: ["type", "text", "autocomplete", "off", 1, "xpo-DateRangeInput-inner"], hostVars: 1, hostBindings: function XpoDateRangeStartInput_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("input", function XpoDateRangeStartInput_input_HostBindingHandler($event) { return ctx.onInput($event.target.value); })("focus", function XpoDateRangeStartInput_focus_HostBindingHandler() { return ctx.onFocus(); })("blur", function XpoDateRangeStartInput_blur_HostBindingHandler() { return ctx.onBlur(); });
    } if (rf & 2) {
        i0.ɵɵattribute("maxlength", ctx.getMaxLength());
    } }, features: [i0.ɵɵProvidersFeature([
            { provide: NG_VALUE_ACCESSOR, useExisting: XpoDateRangeStartInput, multi: true },
            { provide: NG_VALIDATORS, useExisting: XpoDateRangeStartInput, multi: true },
        ]), i0.ɵɵInheritDefinitionFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangeStartInput, [{
        type: Directive,
        args: [{
                selector: 'input[xpoDateRangeStartInput]',
                host: {
                    class: 'xpo-DateRangeInput-inner',
                    type: 'text',
                    autocomplete: 'off',
                    '[attr.maxlength]': 'getMaxLength()',
                    '(input)': 'onInput($event.target.value)',
                    '(focus)': 'onFocus()',
                    '(blur)': 'onBlur()',
                },
                providers: [
                    { provide: NG_VALUE_ACCESSOR, useExisting: XpoDateRangeStartInput, multi: true },
                    { provide: NG_VALIDATORS, useExisting: XpoDateRangeStartInput, multi: true },
                ],
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i1$1.NgForm, decorators: [{
                type: Optional
            }] }, { type: i1$1.FormGroupDirective, decorators: [{
                type: Optional
            }] }, { type: i2.DateAdapter }, { type: undefined, decorators: [{
                type: Inject,
                args: [MAT_DATE_FORMATS]
            }] }, { type: i0.Injector }, { type: i2.ErrorStateMatcher }, { type: XpoDateRangeInputSupressPlaceholder, decorators: [{
                type: Optional
            }] }]; }, null); })();

const _c0 = [[["input", "xpoDateRangeStartInput", ""]], [["input", "xpoDateRangeEndInput", ""]]];
const _c1 = ["input[xpoDateRangeStartInput]", "input[xpoDateRangeEndInput]"];
/**
 * A form fiel that contains two input controls, one for the start date and other for the end date
 */
class XpoDateRangeInput {
    constructor(formField, dateAdapter) {
        this.formField = formField;
        this.dateAdapter = dateAdapter;
        /**
         * Emits when the control's state has changed.
         * Implemented as part of MatFormFieldControl
         */
        this.stateChanges = new Subject();
        /**
         * Unique ID for the control, that MatFormField will associate all its labels and hints.
         * Implemented as part of MatFormFieldControl
         */
        this.id = `xpo-date-range-input-${XpoDateRangeInput.nextId++}`;
        /**
         * Gets the NgControl for this control.
         * Implemented as part of MatFormFieldControl
         */
        this.ngControl = null;
        this.isDisabled = false;
        /**
         * This property allows us to specify a unique string for the type of control in form field.
         * The MatFormField will add an additional class based on this type that can be used to apply special styles.
         * Implemented as part of MatFormFieldControl
         */
        this.controlType = 'xpo-date-range-input';
        /** Separator text to be shown between the inputs. */
        this.separator = '–';
        /** Value for the `aria-describedby` attribute of the input */
        this.ariaDescribedBy = null;
        this.destroy$ = new Subject();
    }
    /**
     * Current value of the range input.
     * Implemented as part of MatFormFieldControl
     */
    get value() {
        return this.selectionModel ? this.selectionModel.selection : null;
    }
    /**
     * The minimum valid date.
     * */
    get min() {
        return this.minDate;
    }
    set min(value) {
        // deserialize the min date to get a valid on or null
        const validDate = this.dateAdapter.getValidDateOrNull(this.dateAdapter.deserialize(value));
        // check if the min date is not the same as the date for the component value
        if (!this.dateAdapter.sameDate(validDate, this.minDate)) {
            this.minDate = validDate;
            // need to run validators again
            this.validate();
        }
    }
    /**
     * The maximum valid date.
     * */
    get max() {
        return this.maxDate;
    }
    set max(value) {
        // deserialize the max date to get a valid on or null
        const validDate = this.dateAdapter.getValidDateOrNull(this.dateAdapter.deserialize(value));
        // check if the min date is not the same as the date for the component value
        if (!this.dateAdapter.sameDate(validDate, this.maxDate)) {
            this.maxDate = validDate;
            // need to run validators again
            this.validate();
        }
    }
    /**
     * The placeholder for this control.
     * Set the placeholder attribute on `xpoDateRangeStart` and `xpoDateRangeEnd` inputs.
     * Implemented as part of MatFormFieldControl
     */
    get placeholder() {
        // TODO: ¿return based on inputs placeholders?
        return null;
    }
    /**
     * Whether the control is focused.
     * Implemented as part of MatFormFieldControl
     */
    get focused() {
        // set the focused state for the form field control
        // based on the state of its children
        // or if the calendar is opened
        return this.startInput.focused || this.endInput.focused || this.dateRangePicker.isOpened();
    }
    /**
     * Whether the control is empty.
     * Implemented as part of MatFormFieldControl
     */
    get empty() {
        // TODO: return based on inputs values?
        return true;
    }
    /**
     * Whether the control's label should try to float.
     * Implemented as part of MatFormFieldControl
     */
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }
    /**
     * Whether the control is required.
     * Implemented as part of MatFormFieldControl
     */
    get required() {
        return this.startInput.required || this.endInput.required;
    }
    /**
     * Whether the control is disabled.
     * Implemented as part of MatFormFieldControl
     * TODO: what happens with inputs disabled property?
     */
    get disabled() {
        return this.isDisabled;
    }
    set disabled(disabled) {
        this.isDisabled = coerceBooleanProperty(disabled);
        // to make sure we run change detection if the disabled state changes
        this.stateChanges.next();
    }
    /**
     * Whether the associated NgControl is in an error state.
     * Implemented as part of MatFormFieldControl
     */
    get errorState() {
        return this.startInput.errorState || this.endInput.errorState;
    }
    /** The date range picker that this input is associated with. */
    get dateRangePicker() {
        return this.picker;
    }
    set dateRangePicker(dateRangePicker) {
        this.picker = dateRangePicker;
        // each time we set a new range picker we have to register the new selection model
        this.registerSelectionModel();
        // associate this component as the as the date picker input
        this.picker.registerInput(this);
    }
    ngAfterContentInit() {
        if (!this.startInput) {
            throw Error('xpo-date-range-input must contain a xpoDateRangeStartInput input');
        }
        if (!this.endInput) {
            throw Error('xpo-date-range-input must contain a xpoDateRangeEndInput input');
        }
        // listen to changes on start and end inputs
        merge(this.startInput.stateChanges, this.endInput.stateChanges)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
            this.stateChanges.next();
        });
        this.startInput.minDate = this.min;
        this.startInput.maxDate = this.max;
        this.endInput.minDate = this.min;
        this.endInput.maxDate = this.max;
        // register the date range picker selection model
        // for this component and its child inputs
        this.registerSelectionModel();
    }
    ngOnDestroy() {
        // make sure to complete stateChanges when the component is destroyed
        this.stateChanges.complete();
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    /**
     * Sets the list of element IDs that currently describe this control.
     * Implemented as part of MatFormFieldControl
     */
    setDescribedByIds(ids) {
        this.ariaDescribedBy = ids.length ? ids.join(' ') : null;
    }
    /**
     * Handles a click on the control's container.
     * Implemented as part of MatFormFieldControl
     */
    onContainerClick() {
        if (!this.disabled && !this.dateRangePicker.isOpened()) {
            this.dateRangePicker.open();
            // we need to set the start and end inputs as touched when opening calendar
            // call the ControlValueAccessor onTouched callback function to allow the forms API to update itself
            this.startInput.onTouched();
            this.endInput.onTouched();
        }
    }
    /**
     * Gets the element to which the calendar overlay should be attached.
     */
    getConnectedOverlayOrigin() {
        return this.formField.getConnectedOverlayOrigin();
    }
    /** Gets the value that is used to mirror the state of the start input. */
    getStartInputMirrorValue() {
        return this.startInput ? this.startInput.getMirrorValue() : '';
    }
    /**
     * Whether the separate text should be hidden.
     **/
    shouldHideSeparator() {
        // if start and end input have not placeholder or values
        // separator should be hidden
        return (this.startInput.getPlaceholder().length === 0 &&
            this.startInput.isEmpty() &&
            this.endInput.getPlaceholder().length === 0 &&
            this.endInput.isEmpty());
    }
    /**
     * Whether to show the separate text with same
     * placeholder styles
     */
    showSeparatorAsPlaceholder() {
        // if start or end input have placeholder and both have empty values
        // separator should be hidden
        return ((this.startInput.getPlaceholder().length > 0 || this.endInput.getPlaceholder().length > 0) &&
            this.startInput.isEmpty() &&
            this.endInput.isEmpty());
    }
    /**
     * Associate the date picker selection model to this component and its children inputs
     */
    registerSelectionModel() {
        this.selectionModel = this.dateRangePicker.selectionModel;
        // add a sanity check in case this is called in the date picker @Input set
        // before the children inputs are initialized
        if (this.startInput) {
            this.startInput.registerSelectionModel(this.selectionModel);
        }
        if (this.endInput) {
            this.endInput.registerSelectionModel(this.selectionModel);
        }
    }
    /**
     * Re-runs the validators on the start/end inputs.
     * */
    validate() {
        if (this.startInput) {
            this.startInput.validatorOnChange();
        }
        if (this.endInput) {
            this.endInput.validatorOnChange();
        }
    }
}
/**
 * Unique ID for the control
 */
XpoDateRangeInput.nextId = 0;
XpoDateRangeInput.ɵfac = function XpoDateRangeInput_Factory(t) { return new (t || XpoDateRangeInput)(i0.ɵɵdirectiveInject(MAT_FORM_FIELD), i0.ɵɵdirectiveInject(i2.DateAdapter)); };
XpoDateRangeInput.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: XpoDateRangeInput, selectors: [["xpo-date-range-input"]], contentQueries: function XpoDateRangeInput_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, XpoDateRangeStartInput, 5);
        i0.ɵɵcontentQuery(dirIndex, XpoDateRangeEndInput, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.startInput = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.endInput = _t.first);
    } }, hostAttrs: ["role", "group", 1, "xpo-DateRangeInput"], hostVars: 2, hostBindings: function XpoDateRangeInput_HostBindings(rf, ctx) { if (rf & 2) {
        i0.ɵɵattribute("id", ctx.id)("aria-describedby", ctx.ariaDescribedBy);
    } }, inputs: { min: "min", max: "max", separator: "separator", dateRangePicker: "dateRangePicker" }, exportAs: ["xpoDateRangeInput"], features: [i0.ɵɵProvidersFeature([{ provide: MatFormFieldControl, useExisting: XpoDateRangeInput }])], ngContentSelectors: _c1, decls: 9, vars: 6, consts: [[1, "xpo-DateRangeInput-wrapper"], [1, "xpo-DateRangeStartInput-wrapper"], ["aria-hidden", "true", 1, "xpo-DateRangeStartInput-mirror"], [1, "xpo-DateRangeInput-separator"], [1, "xpo-DateRangeEndInput-wrapper"]], template: function XpoDateRangeInput_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef(_c0);
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵprojection(2);
        i0.ɵɵelementStart(3, "span", 2);
        i0.ɵɵtext(4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "span", 3);
        i0.ɵɵtext(6);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(7, "div", 4);
        i0.ɵɵprojection(8, 1);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(4);
        i0.ɵɵtextInterpolate(ctx.getStartInputMirrorValue());
        i0.ɵɵadvance(1);
        i0.ɵɵclassProp("xpo-DateRangeInput-separator--hidden", ctx.shouldHideSeparator())("xpo-DateRangeInput-separator--placeholder", ctx.showSeparatorAsPlaceholder());
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate(ctx.separator);
    } }, styles: [".flex--stretch{flex:1}.xpo-DateRangeInput{display:block;width:100%}.xpo-DateRangeInput-wrapper{align-items:center;display:flex;padding:8px 6px}.xpo-DateRangeInput-separator{margin:0 4px;text-align:center;width:10px}.xpo-DateRangeInput-separator--hidden{opacity:0}.xpo-DateRangeInput-separator--placeholder{color:#757575}.xpo-DateRangeInput-inner{border:none;color:#000;font-family:\"Roboto\",\"Helvetica Neue\",Arial,sans-serif;font-size:1rem;line-height:inherit;margin:0;padding:0;resize:none;width:100%}.xpo-DateRangeStartInput-wrapper{max-width:calc(50% - 9px);position:relative}.xpo-DateRangeStartInput-wrapper .xpo-DateRangeInput-inner{left:0;position:absolute;top:0}.xpo-DateRangeStartInput-mirror{display:inline-block;min-width:2px;visibility:hidden;white-space:nowrap}.xpo-DateRangeEndInput-wrapper{max-width:calc(50% - 9px)}\n"], encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangeInput, [{
        type: Component,
        args: [{
                selector: 'xpo-date-range-input',
                exportAs: 'xpoDateRangeInput',
                templateUrl: 'date-range-input.component.html',
                styleUrls: ['date-range-input.component.scss'],
                host: {
                    class: 'xpo-DateRangeInput',
                    role: 'group',
                    '[attr.id]': 'id',
                    '[attr.aria-describedby]': 'ariaDescribedBy',
                },
                // need to override some styles of projected content (inputs controls)
                encapsulation: ViewEncapsulation.None,
                providers: [{ provide: MatFormFieldControl, useExisting: XpoDateRangeInput }],
            }]
    }], function () { return [{ type: i2$1.MatFormField, decorators: [{
                type: Inject,
                args: [MAT_FORM_FIELD]
            }] }, { type: i2.DateAdapter }]; }, { min: [{
            type: Input
        }], max: [{
            type: Input
        }], separator: [{
            type: Input
        }], dateRangePicker: [{
            type: Input
        }], startInput: [{
            type: ContentChild,
            args: [XpoDateRangeStartInput]
        }], endInput: [{
            type: ContentChild,
            args: [XpoDateRangeEndInput]
        }] }); })();

class XpoDateRangePickerToggle {
    open(event) {
        if (this.dateRangePicker) {
            this.dateRangePicker.open();
            event.stopPropagation();
        }
    }
}
XpoDateRangePickerToggle.ɵfac = function XpoDateRangePickerToggle_Factory(t) { return new (t || XpoDateRangePickerToggle)(); };
XpoDateRangePickerToggle.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: XpoDateRangePickerToggle, selectors: [["xpo-date-range-picker-toggle"]], hostAttrs: [1, "xpo-DateRangePickerToggle"], inputs: { dateRangePicker: ["for", "dateRangePicker"] }, exportAs: ["xpoDateRangePickerToggle"], decls: 6, vars: 0, consts: [["mat-icon-button", "", 3, "click"], ["viewBox", "0 0 24 24", "xmlns", "http://www.w3.org/2000/svg"], ["d", "M21 3H18V1H16V3H8V1H6V3H3V21H21V3ZM19 19H5V8H19V19Z", "fill", "#4A4A4A"], ["d", "M7 10H9V12H7V10Z", "fill", "#4A4A4A"], ["d", "M11 10H13V12H11V10Z", "fill", "#4A4A4A"], ["d", "M15 10H17V12H15V10Z", "fill", "#4A4A4A"]], template: function XpoDateRangePickerToggle_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "button", 0);
        i0.ɵɵlistener("click", function XpoDateRangePickerToggle_Template_button_click_0_listener($event) { return ctx.open($event); });
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelementStart(1, "svg", 1);
        i0.ɵɵelement(2, "path", 2);
        i0.ɵɵelement(3, "path", 3);
        i0.ɵɵelement(4, "path", 4);
        i0.ɵɵelement(5, "path", 5);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } }, directives: [i3.MatButton], styles: [".flex--stretch[_ngcontent-%COMP%]{flex:1}.xpo-DateRangePickerToggle[_nghost-%COMP%]   .mat-icon-button[_ngcontent-%COMP%]{margin:0}.xpo-DateRangePickerToggle[_nghost-%COMP%]   .mat-icon-button[_ngcontent-%COMP%]   svg[_ngcontent-%COMP%]{height:18px;width:18px}.xpo-DateRangePickerToggle[_nghost-%COMP%]   .mat-icon-button[_ngcontent-%COMP%]:hover   svg[_ngcontent-%COMP%]   path[_ngcontent-%COMP%], .xpo-DateRangePickerToggle[_nghost-%COMP%]   .mat-icon-button[_ngcontent-%COMP%]:focus   svg[_ngcontent-%COMP%]   path[_ngcontent-%COMP%]{fill:#9b9b9b}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangePickerToggle, [{
        type: Component,
        args: [{
                selector: 'xpo-date-range-picker-toggle',
                templateUrl: 'date-range-picker-toggle.component.html',
                styleUrls: ['date-range-picker-toggle.component.scss'],
                host: {
                    class: 'xpo-DateRangePickerToggle',
                },
                exportAs: 'xpoDateRangePickerToggle',
            }]
    }], null, { dateRangePicker: [{
            type: Input,
            args: ['for']
        }] }); })();

class XpoDateRangeSelectionModel {
    constructor(dateAdapter) {
        this.dateAdapter = dateAdapter;
        /** Notifier for selection changes */
        this.selectionChanged = new Subject();
        /** Current selected range */
        this.selection = new DateRange(null, null);
    }
    /**
     * Changes the current selection based on a new date.
     * The given date fills in the next `null` value in the range. If both the start and the end already have a date,
     * the selection is reset so that the given date is the new `start` and the `end` is null.
     * @param date
     * @param source
     */
    changeSelection(date, source) {
        // keep the curren selection start and end
        let { start, end } = this.selection;
        if (start && !end && date && this.dateAdapter.compareDate(date, start) >= 0) {
            // when only the start is selected
            // and the selected date is later than the start
            // we finished the range
            end = date;
        }
        else {
            // if there is no current selection started or selection is already completed
            // or the new date is earlier than the current start
            // start a new range with the selected date
            start = date;
            end = null;
        }
        // update the current selection with new computed values
        this.updateSelection(new DateRange(start, end), source);
    }
    /**
     * Update the selected range
     * @param selection
     */
    updateSelection(selection, source) {
        // replace the selection
        this.selection = selection;
        // notify the change
        this.selectionChanged.next({ value: this.selection, source });
    }
    /**
     * Checks whether the current selection is complete.
     * This is true if the current selection has a non-null `start` and `end`.
     */
    isComplete() {
        return this.selection.start != null && this.selection.end != null;
    }
}

/**
 * Component responsible for managing the date range picker popup/dialog.
 */
// (from material): We use a component instead of a directive here so the user can use implicit
// template reference variables (e.g. #d vs #d="xpoDateRangePicker"). We can change this to a
// directive if angular adds support for `exportAs: '$implicit'` on directives.
class XpoDateRangePicker {
    constructor(document, overlay, dateAdapter) {
        this.document = document;
        this.overlay = overlay;
        this.dateAdapter = dateAdapter;
        /** Emits when the datepicker has been opened. */
        this.opened = new EventEmitter();
        /** Emits when the datepicker has been closed. */
        this.closed = new EventEmitter();
        /** The input element this datepicker is associated with. */
        this.dateRangeInput = null;
        /** Reference to the overlay when the calendar is opened */
        this.popupRef = null;
        /** The element that was focused before the datepicker was opened. */
        this.focusedElementBeforeOpen = null;
        /** Model for the selected range */
        this.selectionModel = new XpoDateRangeSelectionModel(this.dateAdapter);
    }
    /**
     * Whether the calendar is open.
     **/
    isOpened() {
        return this.popupRef !== null;
    }
    /**
     * Register an input with this date picker
     * @param input
     */
    registerInput(input) {
        this.dateRangeInput = input;
    }
    /**
     * Opens the date range picker popup
     */
    open() {
        // create the popup overlay with the
        // date range calendar component rendered inside it
        this.createPopup();
        // keep the las focused element before open
        // to return de focus after close
        if (this.document) {
            this.focusedElementBeforeOpen = this.document.activeElement;
        }
        // emit the open event
        this.opened.emit();
    }
    /**
     * Closes date range picker popup
     */
    close() {
        this.destroyPopup();
        // after the popup is closed
        // return the focus to the element that has it before open (if it can take the focus)
        if (this.focusedElementBeforeOpen && typeof this.focusedElementBeforeOpen.focus === 'function') {
            this.focusedElementBeforeOpen.focus();
            this.focusedElementBeforeOpen = null;
        }
        // emit the close event
        this.closed.emit();
    }
    /**
     * The minimum selectable date.
     * */
    getMinDate() {
        var _a;
        return (_a = this.dateRangeInput) === null || _a === void 0 ? void 0 : _a.min;
    }
    /**
     * The maximum selectable date.
     * */
    getMaxDate() {
        var _a;
        return (_a = this.dateRangeInput) === null || _a === void 0 ? void 0 : _a.max;
    }
    /**
     * Create the popup that contains the calendar
     */
    createPopup() {
        // create the popup position strategy
        const positionStrategy = this.createPositionStrategy();
        // create the popup configuration object
        const popupConfig = new OverlayConfig({
            positionStrategy: positionStrategy,
            hasBackdrop: true,
            backdropClass: 'mat-overlay-transparent-backdrop',
            panelClass: 'xpo-DateRangePicker-popup',
        });
        // create the popup
        this.popupRef = this.overlay.create(popupConfig);
        // create a portal to the calendar component to be attached and renderer inside the popup
        const calendarPortal = new ComponentPortal(XpoDateRangeCalendar);
        // attach the calendar portal to the popup overlay and keep the calendar component instance
        const calendar = this.popupRef.attach(calendarPortal).instance;
        // set the reference to this date range picker inside the calendar component
        calendar.registerDateRangePicker(this);
        // close the calendar when clicking outside
        this.popupRef.backdropClick().subscribe(() => this.close());
    }
    /**
     * Destroys the popup
     */
    destroyPopup() {
        this.popupRef.dispose();
        this.popupRef = null;
    }
    /**
     * Creates the position strategy for the popup
     */
    createPositionStrategy() {
        // create the position strategy
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.dateRangeInput.getConnectedOverlayOrigin())
            .withFlexibleDimensions(false)
            .withLockedPosition();
        // set the connected positions
        this.setConnectedPositions(positionStrategy);
        return positionStrategy;
    }
    /**
     * Sets the preferred positions where to show the popup
     * @param strategy
     */
    setConnectedPositions(strategy) {
        // set the positions where to open the popup
        strategy.withPositions([
            // below the input, left aligned
            {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
            },
            // above the input, left aligned
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'bottom',
            },
        ]);
    }
}
XpoDateRangePicker.ɵfac = function XpoDateRangePicker_Factory(t) { return new (t || XpoDateRangePicker)(i0.ɵɵdirectiveInject(DOCUMENT, 8), i0.ɵɵdirectiveInject(i1$2.Overlay), i0.ɵɵdirectiveInject(i2.DateAdapter)); };
XpoDateRangePicker.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: XpoDateRangePicker, selectors: [["xpo-date-range-picker"]], outputs: { opened: "opened", closed: "closed" }, exportAs: ["xpoDateRangePicker"], decls: 0, vars: 0, template: function XpoDateRangePicker_Template(rf, ctx) { }, encapsulation: 2 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangePicker, [{
        type: Component,
        args: [{
                selector: 'xpo-date-range-picker',
                template: '',
                exportAs: 'xpoDateRangePicker',
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: i1$2.Overlay }, { type: i2.DateAdapter }]; }, { opened: [{
            type: Output
        }], closed: [{
            type: Output
        }] }); })();

class XpoDateRangePickerModule {
}
XpoDateRangePickerModule.ɵfac = function XpoDateRangePickerModule_Factory(t) { return new (t || XpoDateRangePickerModule)(); };
XpoDateRangePickerModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: XpoDateRangePickerModule });
XpoDateRangePickerModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [{ provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_DATE_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] }], imports: [[CommonModule, MatDatepickerModule, MatButtonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDateRangePickerModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, MatDatepickerModule, MatButtonModule],
                declarations: [
                    XpoDateRangeCalendarHeader,
                    XpoDateRangeCalendar,
                    XpoDateRangeInput,
                    XpoDateRangeInputSupressPlaceholder,
                    XpoDateRangeStartInput,
                    XpoDateRangeEndInput,
                    XpoDateRangePickerToggle,
                    XpoDateRangePicker,
                ],
                exports: [XpoDateRangeInput, XpoDateRangeInputSupressPlaceholder, XpoDateRangeStartInput, XpoDateRangeEndInput, XpoDateRangePickerToggle, XpoDateRangePicker],
                entryComponents: [XpoDateRangeCalendarHeader, XpoDateRangeCalendar],
                providers: [{ provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_DATE_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] }],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(XpoDateRangePickerModule, { declarations: [XpoDateRangeCalendarHeader,
        XpoDateRangeCalendar,
        XpoDateRangeInput,
        XpoDateRangeInputSupressPlaceholder,
        XpoDateRangeStartInput,
        XpoDateRangeEndInput,
        XpoDateRangePickerToggle,
        XpoDateRangePicker], imports: [CommonModule, MatDatepickerModule, MatButtonModule], exports: [XpoDateRangeInput, XpoDateRangeInputSupressPlaceholder, XpoDateRangeStartInput, XpoDateRangeEndInput, XpoDateRangePickerToggle, XpoDateRangePicker] }); })();

/**
 * Generated bundle index. Do not edit.
 */

export { XpoDateRangeCalendar, XpoDateRangeCalendarHeader, XpoDateRangeEndInput, XpoDateRangeInput, XpoDateRangeInputSupressPlaceholder, XpoDateRangePicker, XpoDateRangePickerModule, XpoDateRangePickerToggle, XpoDateRangeSelectionModel, XpoDateRangeStartInput };
